/**
 * Tech Pack PREVIEW — the pre-generation screen reached from the canvas
 * (PRD design-studio.md story 2). Assembles the design's loaded revision +
 * its Style (ADR-0044) into the shared TechPackDocument. "Generate" records
 * a pack row and DEBITS AI CREDITS (R13); insufficient balance shows honest
 * top-up copy and never touches the canvas.
 *
 * No editing affordances here, ever — changes happen on the canvas and
 * produce a new revision (AC7).
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, Download, FileText, Loader2, Printer } from "lucide-react";
import { Alert, Button, StatusPill, toast } from "@flint/ui";
import { trpc } from "../../../../lib/trpc.js";
import { openDocPdf } from "../../../../lib/pdf.js";
import { TechPackDocument } from "./tech-pack-document.tsx";

export function TechPackPage({ styleId }: { styleId: string }) {
  const utils = trpc.useUtils();
  const design = trpc.garmentDesigns.getForStyle.useQuery({ styleId });
  const style = trpc.styles.get.useQuery({ id: styleId });

  const [generated, setGenerated] = useState<{
    packId: string;
    version: number;
    creditsDebited: number;
    balanceAfter: number;
  } | null>(null);
  const [creditsBlocked, setCreditsBlocked] = useState<string | null>(null);

  const generate = trpc.garmentDesigns.generatePack.useMutation({
    onSuccess: async (result) => {
      setGenerated(result);
      setCreditsBlocked(null);
      await utils.garmentDesigns.getForStyle.invalidate({ styleId });
      toast.success("Tech pack generated", {
        description: `Built from v${result.version} · ${result.creditsDebited} AI credit debited · balance ${result.balanceAfter}.`,
      });
    },
    onError: (err) => {
      if (err.message.includes("credits")) {
        setCreditsBlocked(err.message);
      } else {
        toast.error("Couldn't generate the tech pack", { description: err.message });
      }
    },
  });

  if (design.isLoading || style.isLoading) {
    return <p className="p-6 text-sm text-muted">Assembling tech pack…</p>;
  }
  if (design.error || !design.data || !style.data) {
    return (
      <div className="p-6">
        <Alert
          intent="bad"
          title="Couldn't load the design"
          description={design.error?.message ?? style.error?.message ?? "Not found."}
        />
      </div>
    );
  }

  const d = design.data;
  const s = style.data;

  return (
    <div className="flex min-h-full flex-col bg-bg">
      {/* Head — hidden in print */}
      <header className="sticky top-0 z-20 flex h-14 items-center gap-3.5 border-b border-line bg-paper px-6 print:hidden">
        <Link
          to="/merchandising/styles/$styleId/visual"
          params={{ styleId }}
          search={{ tab: "design" as const }}
          aria-label="Back to the design"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-sm border border-line text-ink-2 transition hover:bg-bg"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 text-base font-bold leading-tight tracking-tight text-ink">
            Tech pack
            <span className="rounded-full bg-raised px-2 py-0.5 font-mono text-xs font-medium text-muted">
              {d.styleRef} · v{d.currentVersion}
            </span>
            {generated ? (
              <StatusPill tone="ok" size="sm" dot>
                Generated
              </StatusPill>
            ) : (
              <StatusPill tone="muted" size="sm">
                Preview
              </StatusPill>
            )}
          </h1>
          <p className="mt-0.5 text-xs text-muted">
            Read-only — edits happen on the canvas and become a new revision.
          </p>
        </div>
        <Button
          intent="ghost"
          size="sm"
          className="border border-line bg-paper"
          disabled={!generated}
          onClick={() => window.print()}
        >
          <Printer size={14} />
          Print
        </Button>
        <Button
          intent="ghost"
          size="sm"
          className="border border-line bg-paper"
          disabled={!generated}
          onClick={() => {
            if (!generated) return;
            openDocPdf(`/api/tech-packs/${generated.packId}/pdf`, {
              download: true,
              filename: `tech-pack-${d.styleRef.replace(/\//g, "-")}-v${generated.version}`,
            });
          }}
        >
          <Download size={14} />
          Download PDF
        </Button>
        <Button size="sm" disabled={generate.isPending} onClick={() => generate.mutate({ designId: d.id })}>
          {generate.isPending ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
          Generate (1 credit)
        </Button>
      </header>

      {creditsBlocked ? (
        <div className="mx-auto w-full max-w-3xl px-6 pt-4 print:hidden">
          <Alert
            intent="warn"
            title="Not enough AI credits"
            description="Tech pack generation uses 1 AI credit. Top up in Settings → Billing to keep generating — your canvas and saved revisions are untouched."
          />
        </div>
      ) : null}

      <TechPackDocument
        model={{
          styleRef: d.styleRef,
          buyerName: d.buyerName,
          garmentType: d.garmentType,
          name: d.name,
          poNumber: s.poNumber,
          exFactoryDate: s.exFactoryDate,
          lines: s.lines.map((line) => ({ size: line.size, qty: line.qty })),
          elements: d.elements,
        }}
        emptyHint="No markings yet — add elements on the canvas; the workorder page below still reflects the Style File."
        provenance={
          generated
            ? `Generated from revision v${generated.version} · pack ${generated.packId.slice(0, 8)}`
            : "Preview — generate to stamp a revision and enable print."
        }
      />
    </div>
  );
}
